import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, TrendingUp, Calendar, Wallet, Download, Filter, X, CalendarDays } from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";
import config from "../../config";

export default function RevenueStats() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const [isDownloading, setIsDownloading] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    async function fetchRevenueStats() {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("adminAuthToken");
        
        const response = await axios.get(
          `${config.url}/api/admin/dashboard/stats`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.responseCode === 200) {
          setStats(response.data.responseData);
        } else {
          setError("Failed to fetch revenue statistics");
        }
      } catch (err) {
        console.error("Error fetching revenue stats:", err);
        setError("Unable to load revenue data");
      } finally {
        setIsLoading(false);
      }
    }

    fetchRevenueStats();
  }, []);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      const token = localStorage.getItem("adminAuthToken");
      
      // Validate custom date range
      if (selectedFilter === "CUSTOM") {
        if (!customStartDate || !customEndDate) {
          alert("Please select both start and end dates for custom range");
          setIsDownloading(false);
          return;
        }
        if (new Date(customStartDate) > new Date(customEndDate)) {
          alert("Start date cannot be after end date");
          setIsDownloading(false);
          return;
        }
      }

      const params = { filterType: selectedFilter };
      if (selectedFilter === "CUSTOM") {
        params.startDate = new Date(customStartDate).toISOString();
        params.endDate = new Date(customEndDate + "T23:59:59").toISOString();
      }
      
      const response = await axios.get(
        `${config.url}/api/admin/dashboard/revenue/detailed`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.responseCode === 200) {
        const revenueData = response.data.responseData;
        
        if (!revenueData || revenueData.length === 0) {
          alert("No revenue data available for the selected filter");
          return;
        }

        // Prepare data for Excel
        const excelData = revenueData.map((payment, index) => ({
          "Sr. No": index + 1,
          "Payment ID": payment.paymentId || "N/A",
          "Order ID": payment.orderId || "N/A",
          "User ID": payment.userId || "N/A",
          "Amount (₹)": payment.amount || 0,
          "Status": payment.status || "N/A",
          "Subscription Type": payment.subscriptionType || "N/A",
          "Plan ID": payment.planId || "N/A",
          "Package ID": payment.packageId || "N/A",
          "Coupon ID": payment.couponId || "N/A",
          "Created At": payment.createdAt ? formatDateTime(payment.createdAt) : "N/A",
          "Updated At": payment.updatedAt ? formatDateTime(payment.updatedAt) : "N/A",
        }));

        // Calculate total revenue
        const totalRevenue = revenueData.reduce((sum, p) => sum + (p.amount || 0), 0);

        // Add summary row immediately after data (no blank row)
        excelData.push({
          "Sr. No": "",
          "Payment ID": "",
          "Order ID": "TOTAL",
          "User ID": "",
          "Amount (₹)": totalRevenue,
          "Status": `${revenueData.length} payments`,
          "Subscription Type": "",
          "Plan ID": "",
          "Package ID": "",
          "Coupon ID": "",
          "Created At": "",
          "Updated At": "",
        });

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue Data");

        // Style the worksheet
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + "1";
          if (!worksheet[address]) continue;
          worksheet[address].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "4F81BD" } },
          };
        }

        // Set column widths
        worksheet['!cols'] = [
          { wch: 8 },  // Sr. No
          { wch: 15 }, // Payment ID
          { wch: 25 }, // Order ID
          { wch: 10 }, // User ID
          { wch: 12 }, // Amount
          { wch: 12 }, // Status
          { wch: 18 }, // Subscription Type
          { wch: 10 }, // Plan ID
          { wch: 12 }, // Package ID
          { wch: 12 }, // Coupon ID
          { wch: 20 }, // Created At
          { wch: 20 }, // Updated At
        ];

        // Generate filename with filter and date
        let filterName = selectedFilter.replace(/_/g, " ");
        if (selectedFilter === "CUSTOM") {
          filterName = `${customStartDate}_to_${customEndDate}`;
        }
        const fileName = `Revenue_${filterName}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download file
        XLSX.writeFile(workbook, fileName);
        
        setShowFilterModal(false);
        // Download happens automatically without alert
      } else {
        alert("Failed to fetch revenue data");
      }
    } catch (err) {
      console.error("Error downloading Excel:", err);
      alert("Error downloading Excel file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const filterOptions = [
    { value: "ALL", label: "All Time", icon: "📊" },
    { value: "LAST_24_HOURS", label: "Last 24H", icon: "⏰" },
    { value: "DAILY", label: "Today", icon: "📅" },
    { value: "MONTHLY", label: "This Month", icon: "📆" },
    { value: "QUARTERLY", label: "Quarter", icon: "📈" },
    { value: "YEARLY", label: "This Year", icon: "🗓️" },
    { value: "CUSTOM", label: "Custom Range", icon: "🎯" },
  ];

  const revenueCards = [
    {
      label: "Total Revenue",
      value: stats?.totalRevenue,
      icon: <Wallet size={18} />,
      gradient: "from-blue-500 to-blue-600",
      bg: "bg-blue-50 dark:bg-blue-500/10",
      text: "text-blue-700 dark:text-blue-400",
      border: "border-blue-200 dark:border-blue-500/20",
    },
    {
      label: "Today's Revenue",
      value: stats?.todayRevenue,
      icon: <DollarSign size={18} />,
      gradient: "from-green-500 to-green-600",
      bg: "bg-green-50 dark:bg-green-500/10",
      text: "text-green-700 dark:text-green-400",
      border: "border-green-200 dark:border-green-500/20",
    },
    {
      label: "This Month",
      value: stats?.monthRevenue,
      icon: <Calendar size={18} />,
      gradient: "from-purple-500 to-purple-600",
      bg: "bg-purple-50 dark:bg-purple-500/10",
      text: "text-purple-700 dark:text-purple-400",
      border: "border-purple-200 dark:border-purple-500/20",
    },
    {
      label: "This Year",
      value: stats?.yearRevenue,
      icon: <TrendingUp size={18} />,
      gradient: "from-orange-500 to-orange-600",
      bg: "bg-orange-50 dark:bg-orange-500/10",
      text: "text-orange-700 dark:text-orange-400",
      border: "border-orange-200 dark:border-orange-500/20",
    },
  ];

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-6">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Download Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Overview</h2>
        <button
          onClick={() => setShowFilterModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
        >
          <Download size={18} />
          <span className="hidden sm:inline">Download Excel</span>
          <span className="sm:hidden">Excel</span>
        </button>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {revenueCards.map((card, index) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{ scale: 1.02, y: -2 }}
            className={`relative overflow-hidden rounded-xl border ${card.border} ${card.bg} p-6 transition-all duration-200 hover:shadow-lg`}
          >
            {/* Gradient overlay */}
            <div
              className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${card.gradient} opacity-5 rounded-full -mr-8 -mt-8`}
            ></div>

            <div className="relative">
              {/* Icon */}
              <div className={`inline-flex p-2.5 rounded-lg ${card.bg} mb-4`}>
                <div className={card.text}>{card.icon}</div>
              </div>

              {/* Label */}
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                {card.label}
              </p>

              {/* Value */}
              {isLoading ? (
                <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              ) : (
                <h3 className={`text-2xl font-bold ${card.text}`}>
                  {formatCurrency(card.value)}
                </h3>
              )}

              {/* Additional info */}
              {!isLoading && stats && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {card.label === "Total Revenue" && `${stats.successfulPayments} payments`}
                    {card.label === "Today's Revenue" && "Last 24 hours"}
                    {card.label === "This Month" && new Date().toLocaleDateString('en-US', { month: 'long' })}
                    {card.label === "This Year" && new Date().getFullYear()}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Compact Horizontal Filter Modal */}
      <AnimatePresence>
        {showFilterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowFilterModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Filter className="text-green-600" size={22} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Select Revenue Period
                  </h3>
                </div>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={18} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Horizontal Filter Options */}
              <div className="p-5">
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedFilter(option.value)}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                        selectedFilter === option.value
                          ? "border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md"
                          : "border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-700"
                      }`}
                    >
                      <span className="text-2xl">{option.icon}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white text-center">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Custom Date Range Picker */}
                {selectedFilter === "CUSTOM" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <CalendarDays size={16} className="inline mr-1" />
                          From Date
                        </label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          max={customEndDate || new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <CalendarDays size={16} className="inline mr-1" />
                          To Date
                        </label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          min={customStartDate}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDownloadExcel}
                  disabled={isDownloading}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  {isDownloading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      <span>Download Excel</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
